import { ALL_INVOICE_PAGE_SIZES, type InvoicePageSizeId } from "@/constants/invoice";
import { BILLING_UI_ENABLED, PLAN_DESCRIPTIONS, PLAN_LABELS } from "@/constants/plans";
import { PageHeader } from "@/components/shared/page-header";
import { PlanBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select } from "@/components/ui/field";
import { useUpdateWorkspaceSettings } from "@/features/workspaces/api";
import { detectTimezone } from "@/lib/preferences";
import { useWorkspace } from "@/providers/workspace-provider";
import { useEffect, useState } from "react";
import { toast } from "sonner";

/** `/w/:slug/settings` — workspace profile, invoice defaults and plan. */
export const SettingsPage = () => {
  const { workspace, settings } = useWorkspace();
  const updateSettings = useUpdateWorkspaceSettings(workspace.id);

  const [form, setForm] = useState({
    invoice_prefix: settings.invoice_prefix,
    invoice_page_size: settings.invoice_page_size,
    currency: settings.currency,
    tax_percent: String(settings.tax_percent),
    address: settings.address ?? "",
    phone: settings.phone ?? "",
    whatsapp_number: settings.whatsapp_number ?? "",
    timezone: settings.timezone,
  });

  useEffect(() => {
    setForm({
      invoice_prefix: settings.invoice_prefix,
      invoice_page_size: settings.invoice_page_size,
      currency: settings.currency,
      tax_percent: String(settings.tax_percent),
      address: settings.address ?? "",
      phone: settings.phone ?? "",
      whatsapp_number: settings.whatsapp_number ?? "",
      timezone: settings.timezone,
    });
  }, [settings]);

  const onSave = async () => {
    try {
      await updateSettings.mutateAsync({
        invoice_prefix: form.invoice_prefix,
        invoice_page_size: form.invoice_page_size as InvoicePageSizeId,
        currency: form.currency.toUpperCase(),
        tax_percent: Number(form.tax_percent) || 0,
        address: form.address.trim().length > 0 ? form.address.trim() : null,
        phone: form.phone.trim().length > 0 ? form.phone.trim() : null,
        whatsapp_number:
          form.whatsapp_number.trim().length > 0 ? form.whatsapp_number.trim() : null,
        timezone: form.timezone,
      });
      toast.success("Settings saved.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save the settings.");
    }
  };

  return (
    <>
      <PageHeader
        title="Workspace settings"
        description={`/${workspace.slug}`}
        actions={
          <Button loading={updateSettings.isPending} onClick={() => void onSave()}>
            Save changes
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Invoice defaults</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Number prefix" htmlFor="settings-prefix">
                <Input
                  id="settings-prefix"
                  value={form.invoice_prefix}
                  onChange={(event) => setForm({ ...form, invoice_prefix: event.target.value })}
                />
              </Field>
              <Field label="Default page size" htmlFor="settings-page-size">
                <Select
                  id="settings-page-size"
                  value={form.invoice_page_size}
                  onChange={(event) =>
                    setForm({ ...form, invoice_page_size: event.target.value as InvoicePageSizeId })
                  }
                >
                  {ALL_INVOICE_PAGE_SIZES.map((size) => (
                    <option key={size.id} value={size.id}>
                      {size.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Currency" htmlFor="settings-currency">
                <Input
                  id="settings-currency"
                  value={form.currency}
                  onChange={(event) => setForm({ ...form, currency: event.target.value })}
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Tax percent" htmlFor="settings-tax">
                <Input
                  id="settings-tax"
                  inputMode="decimal"
                  value={form.tax_percent}
                  onChange={(event) => setForm({ ...form, tax_percent: event.target.value })}
                />
              </Field>
              <Field
                label="Timezone"
                htmlFor="settings-timezone"
                hint={`Browser: ${detectTimezone()}`}
              >
                <Input
                  id="settings-timezone"
                  value={form.timezone}
                  onChange={(event) => setForm({ ...form, timezone: event.target.value })}
                />
              </Field>
            </div>

            <Field label="Business address" htmlFor="settings-address">
              <Input
                id="settings-address"
                value={form.address}
                onChange={(event) => setForm({ ...form, address: event.target.value })}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Phone" htmlFor="settings-phone">
                <Input
                  id="settings-phone"
                  value={form.phone}
                  onChange={(event) => setForm({ ...form, phone: event.target.value })}
                />
              </Field>
              <Field label="WhatsApp number" htmlFor="settings-whatsapp">
                <Input
                  id="settings-whatsapp"
                  value={form.whatsapp_number}
                  onChange={(event) => setForm({ ...form, whatsapp_number: event.target.value })}
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>Plan</CardTitle>
            <PlanBadge plan={workspace.plan} />
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm font-medium">{PLAN_LABELS[workspace.plan]}</p>
            <p className="text-sm text-muted-foreground">{PLAN_DESCRIPTIONS[workspace.plan]}</p>
            <Button disabled={!BILLING_UI_ENABLED} className="w-full" variant="outline">
              {BILLING_UI_ENABLED ? "Manage subscription" : "Checkout coming soon"}
            </Button>
            <p className="text-xs text-muted-foreground">
              Limits are enforced by the API:{" "}
              {workspace.plan === "free"
                ? "25 products, 20 orders/day, 1 member"
                : "unlimited products, orders and members"}
              .
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
